import React from 'react';

// Using React.FC to ensure proper component typing and children handling
interface ContactInfoItemProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const ContactInfoItem: React.FC<ContactInfoItemProps> = ({ icon, title, children }) => (
  <div className="flex items-start space-x-4">
    <div className="flex-shrink-0 mt-1">
      <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-blue-100 text-blue-600">
        {icon}
      </div>
    </div>
    <div>
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <div className="mt-1 text-gray-600">{children}</div>
    </div>
  </div>
);

interface ContactProps {
  onBookDemo: () => void;
}

const Contact: React.FC<ContactProps> = ({ onBookDemo }) => {
  return (
    <section id="contact-us" className="py-10 md:py-16 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 data-animation="fade-in-up" className="text-2xl md:text-3xl font-bold text-gray-900">Let's Get In Touch!</h2>
          <p data-animation="fade-in-up" style={{ transitionDelay: '200ms' }} className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
            We're here to help and answer any question you might have. We look forward to hearing from you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Column: Map */}
          <div data-animation="fade-in-left" style={{ transitionDelay: '400ms' }} className="w-full h-96 md:h-full min-h-[450px] rounded-[2.5rem] overflow-hidden shadow-lg border border-gray-200 bg-white p-2">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d15555.774591461996!2d77.6074127!3d12.8952877!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae15cfd71597f7%3A0x6e5371c8dc85ee36!2sOmniView%20Labs!5e0!3m2!1sen!2sin!4v1715456428392!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              title="OmniView Labs Location"
              className="min-h-[450px] rounded-[2rem]"
            ></iframe>
          </div>

          {/* Right Column: Contact Details */}
          <div data-animation="fade-in-right" style={{ transitionDelay: '400ms' }} className="space-y-8">
            <ContactInfoItem
              icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
              title="Location"
            >
              <p className="font-bold text-gray-900">OmniView Labs</p>
              <p>Ranka Colony Road, Bangalore, India</p>
            </ContactInfoItem>

            <ContactInfoItem
              icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
              title="Email Us"
            >
              <a href="mailto:info@omniviewlabs.com" className="hover:text-blue-600 transition-colors font-medium">info@omniviewlabs.com</a>
            </ContactInfoItem>

            <ContactInfoItem
              icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
              title="Call Us"
            >
              <a href="tel:+919902594100" className="hover:text-blue-600 transition-colors font-medium">+91 99025 94100</a>
            </ContactInfoItem>

            <div className="pt-4">
              <button
                onClick={onBookDemo}
                className="inline-block w-full text-center font-black uppercase tracking-widest text-white py-5 px-8 rounded-full transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-[#003B5C]/20 bg-[#003B5C] hover:bg-[#002840]"
              >
                Book Your Demo
              </button>
            </div>

            <div>
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Follow Our Journey</h3>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/company/omniviewlabs" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-200 text-gray-500 hover:bg-[#003B5C] hover:text-white transition-all"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg></a>
                <a href="https://www.facebook.com/omniview.labs.official" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-200 text-gray-500 hover:bg-[#003B5C] hover:text-white transition-all"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" /></svg></a>
                <a href="https://www.instagram.com/omniview_labs" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-200 text-gray-500 hover:bg-[#FF5C00] hover:text-white transition-all"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg></a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;